/**
 * NodeIdentityChips — chip set for the Node Inspector identity card.
 *
 * Renders Risk Score, OFAC, Velocity, Escalation, Nearest Exchange, Bridge,
 * Cross-chain, P2P Merchant, and Node Label chips. Same data sources as the
 * legacy TriageRibbon (useTriageStore + useInvestigationStore +
 * useBridgeInsightStore) — but consolidated inside the inspector header so
 * the standalone ribbon panel can be retired and the canvas reclaims that
 * vertical real estate.
 *
 * Click handlers route to the right inspector tab/section instead of opening
 * a separate panel.
 */
import React, { useState, useCallback, useMemo } from 'react';
import { AlertTriangle, ShieldCheck, Network, Globe2, HelpCircle, RefreshCw, Wallet, Banknote, Snowflake, ShieldAlert, Zap } from 'lucide-react';
import { toast } from 'sonner';
import { entityService } from '../../services/api';
import { useGraphStore } from '../../stores/useGraphStore';
import { useTriageStore } from '../../stores/useTriageStore';
import { useInvestigationStore } from '../../stores/useInvestigationStore';
import { useBridgeInsightStore } from '../../stores/useBridgeInsightStore';
import { useSelectionStore } from '../../stores/useSelectionStore';
import { safeStr } from '../../utils/safeStr';
import { EntityNameWithCopy } from './EntityNameWithCopy';
import { InfoHint } from '../InfoHint';
import { Skeleton } from '../ui/Skeleton';
import {
    CONFIRMATION_LABEL, LABEL_CLASS_LABEL, SOURCE_LABEL, WALLET_ROLE_LABEL, customerFacingName,
} from '../../constants/walletRole';

const CHIP =
    'inline-flex items-center gap-1 px-2 py-1 rounded text-xs font-semibold border transition-opacity';
const CHIP_BTN = `${CHIP} hover:opacity-80`;

const CHIP_HINTS = {
    risk: 'Composite risk for this wallet (0–10). Green = lower concern, amber = medium, red = high / critical. Click to open Overview.',
    ofac: 'OFAC / sanctions hit — treat as escalate. Click to open Intel → Sanctions Screening.',
    genesisFunder: 'Genesis funder / initial gas source for downstream activity. Key origin-of-funds and exchange subpoena target.',
    issuerFrozen: 'Address has been frozen / blacklisted on-chain by the token contract issuer (Tether/Circle). Funds are immutably locked.',
    freezeTarget: 'Illicit laundering pool / Black USDT sink. Priority target for emergency Section 91/94 notice to Tether/Circle Compliance.',
    p2p: 'Linked to a peer-to-peer crypto merchant (common cash-out / mule path in India cases).',
    telegram: 'This wallet appears in scraped Telegram channel messages (harvest index).',
    instagram: 'This wallet was harvested from an Instagram OSINT profile (bio/screenshot/live fetch).',
    nearestExchange: 'Closest known exchange this wallet connects to, and hop count. Click to open Intel.',
    velocity: 'How this wallet moves funds: Relay (in≈out fast), Accumulator (holds), Distributor (fans out), etc.',
    escalation: 'Auto-Investigate recommendation: ESCALATE / INVESTIGATE / MONITOR. Click to open Analysis.',
    bridge: 'Bridge hops move value across chains (e.g. TRX → ETH). Click Intel for bridge detail.',
    chains: 'This address (or linked identities) appears active on multiple chains.',
};

// WB09: a visible marker on chips that are heuristics, not facts. The hover line
// names the basis so an officer can tell a path search or an ad sighting from an
// attribution.
const HEURISTIC_BASIS = {
    nearestExchange: 'Heuristic: the closest exchange a path search found within a time budget. A longer search or more loaded transactions can change it; it is not an attribution of this wallet.',
    p2p: 'Heuristic: a P2P advertisement naming this wallet was seen. An ad is a lead, not proof that the wallet is a merchant or that the activity here is P2P trading.',
    velocity: 'Heuristic: a profile from the auto-investigation over the transactions it read (see the Coverage section of the report for the window). It describes behaviour, not intent.',
} as const;

function HeuristicMarker({ basis, kind }: { basis: string; kind: string }) {
    return (
        <span
            data-testid={`heuristic-marker-${kind}`}
            className="ml-0.5 px-1 rounded border border-current/30 text-[9px] font-medium normal-case opacity-80"
            title={basis}
        >
            heuristic
        </span>
    );
}

// Display-only mirror of backend entity_constants.is_generic_cluster_label.
// An OKLink "<brand>. User" / "<brand>_User_v1" tag means the address is a
// COUNTERPARTY of the brand's cluster, not brand-owned — so showing it with a
// blue "verified entity" ShieldCheck chip contradicts exchange_detection
// (which correctly reports is_exchange=FALSE). We append a clarifying badge.
// Owner-role names (Deposit/Hot/Cold/Withdraw/Settlement/Gas) are exempt.
const _CLUSTER_USER_RE = /[.\s\-_]+users?(?:[.\s\-_]*v?\d+)?\s*$/i;
const _CLUSTER_OWNER_RE = /(deposit|hot[\s_]?wallet|cold|withdraw|settlement|gas)/i;
function isGenericClusterLabel(name?: string | null): boolean {
    if (!name || typeof name !== 'string') return false;
    if (_CLUSTER_OWNER_RE.test(name)) return false;
    return _CLUSTER_USER_RE.test(name);
}

const walletRoleClass = (role: string): string => {
    if (role === 'exchange_deposit') {
        return 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-300 border-emerald-300 dark:border-emerald-800';
    }
    if (role === 'exchange_hot') {
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-800';
    }
    if (role === 'customer_wallet') {
        return 'bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 border-violet-300 dark:border-violet-800';
    }
    if (role === 'card_offramp') {
        return 'bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-200 border-pink-300 dark:border-pink-800';
    }
    if (role === 'issuer') {
        return 'bg-slate-100 text-slate-700 dark:bg-slate-800/60 dark:text-slate-300 border-slate-300 dark:border-slate-700';
    }
    return 'bg-secondary text-muted-foreground border-border';
};

interface Props {
    targetAddress: string | null;
    targetChain: string | null;
    onRiskClick?: () => void;
    onSanctionsClick?: () => void;
    onExchangeClick?: () => void;
    onEscalationClick?: () => void;
    onBridgeClick?: () => void;
    onCrossChainClick?: () => void;
}

export const NodeIdentityChips: React.FC<Props> = ({
    targetAddress,
    targetChain,
    onRiskClick,
    onSanctionsClick,
    onExchangeClick,
    onEscalationClick,
    onBridgeClick,
    onCrossChainClick,
}) => {
    const triageData = useTriageStore(s => s.triageData);
    const triageLoading = useTriageStore(s => s.triageLoading);
    const investigation = useInvestigationStore(s => s.investigation);
    const selectedNodeId = useSelectionStore(s => s.selectedNodeId);
    const addr = selectedNodeId || targetAddress;

    const bridgeInsight = useBridgeInsightStore(s =>
        targetChain && addr ? s.bridges[`${targetChain}:${addr}`] : null);
    const crossChainInsight = useBridgeInsightStore(s =>
        targetChain && addr ? s.crossChain[`${targetChain}:${addr}`] : null);

    // F-3b: force re-resolve entity attribution, bypassing the 24h negative cache
    // and re-querying live sources (Arkham etc.). Patches the canvas element + the
    // triage summary so the chip updates without a full triage re-run.
    const [reResolving, setReResolving] = useState(false);
    const reResolveEntity = useCallback(async () => {
        if (!addr || reResolving) return;
        setReResolving(true);
        const tid = toast.loading('Re-resolving (bypassing cache + live sources)…');
        try {
            const map: any = await entityService.batchLookup([{ address: addr, chain: targetChain || '' }], { deep: true });
            const entity: any = map?.[addr.toLowerCase()];
            if (!entity || !entity.name) {
                toast.info('No attribution found — re-checked the entity DB + live sources with the cache bypassed.', { id: tid });
                return;
            }
            const labels = Array.isArray(entity.node_labels) && entity.node_labels.length ? entity.node_labels : [entity.name];
            useGraphStore.getState().enrichElements([{ id: addr, data: {
                entity_name: entity.name, entity_type: entity.category, entity_category: entity.category,
                risk_level: entity.risk_level, entity_tags: entity.tags, node_labels: labels,
                intelligence_attributions: entity.intelligence_attributions || [], entity_source: entity.source,
            } }]);
            useTriageStore.getState().patchEntity(addr, { entity_name: entity.name, entity_type: entity.category, entity_tags: entity.tags });
            toast.success(`Resolved: ${entity.name}`, { id: tid });
        } catch {
            toast.error('Re-resolve failed', { id: tid });
        } finally {
            setReResolving(false);
        }
    }, [addr, targetChain, reResolving]);

    const elements = useGraphStore(st => st.elements);
    const nodeData = useMemo(() => {
        if (!addr) return null;
        const lower = addr.toLowerCase();
        const found = elements.find(
            e => e.data?.id === addr || e.data?.id?.toLowerCase() === lower || e.data?.address?.toLowerCase() === lower
        );
        return found?.data || null;
    }, [elements, addr]);

    if (!addr) return null;
    if (!triageData?.summary && !nodeData) {
        if (!triageLoading) return null;
        return (
            <div className="flex flex-wrap items-center gap-1.5 mt-2" role="status" aria-label="Loading identity chips">
                <Skeleton className="h-6 w-16" />
                <Skeleton className="h-6 w-20" />
                <Skeleton className="h-6 w-24" />
            </div>
        );
    }

    const s = triageData?.summary || {};
    const labelClass = String(nodeData?.label_class || s.label_class || '');
    const walletRole = String(nodeData?.wallet_role || s.wallet_role || '');
    const confirmation = String(nodeData?.confirmation_state || s.confirmation_state || '');
    const sourceKey = String(nodeData?.source || s.source || '').toLowerCase();
    const brand = String(nodeData?.brand || s.brand || '');
    const customerName = customerFacingName({
        wallet_role: walletRole, label_class: labelClass, brand, entity_name: s.entity_name,
    });
    const namedByClass = labelClass.length > 0;
    const clusterTag = !namedByClass && isGenericClusterLabel(s.entity_name);
    const nodeLabels = triageData?.node_labels || [];
    const isHighRisk = s.risk_level === 'high' || s.risk_level === 'critical';
    const isSanctioned = !!s.sanctioned;

    const isGenesisFunder = Boolean(
        nodeData?.placement_role === 'source' ||
        nodeData?.is_genesis_funder ||
        nodeData?.funder_address ||
        nodeData?.funding_source ||
        (Array.isArray(nodeData?.labels) && nodeData.labels.includes('gas_funder')) ||
        (s as any)?.is_genesis_funder ||
        (s as any)?.funder_address ||
        (s as any)?.funding_source
    );
    const funderEntity =
        nodeData?.funding_source?.funder_entity ||
        (s as any)?.funding_source?.funder_entity ||
        nodeData?.funder_entity ||
        (s as any)?.funder_entity;

    const isIssuerFrozen = Boolean(
        nodeData?.is_frozen === true ||
        nodeData?.freeze_status?.is_frozen === true ||
        (s as any)?.is_frozen === true ||
        (s as any)?.freeze_status?.is_frozen === true
    );
    const frozenBy =
        nodeData?.freeze_status?.frozen_by ||
        (s as any)?.freeze_status?.frozen_by ||
        (nodeData?.frozen_by ? [nodeData.frozen_by] : null);

    const isFreezeTarget = !isIssuerFrozen && Boolean(
        nodeData?.issuer_freeze_recommended ||
        nodeData?.black_usdt ||
        nodeData?.freeze_status?.freeze_recommended ||
        (Array.isArray(nodeData?.labels) && (nodeData.labels.includes('black_usdt') || nodeData.labels.includes('freeze_target'))) ||
        (s as any)?.issuer_freeze_recommended ||
        (s as any)?.freeze_status?.freeze_recommended
    );

    // "Checks ran, nothing found" signal — the R10 CLEAR-vs-NOT-TESTED
    // distinction applied to entity attribution. By the time this component
    // renders, triage has completed (guarded by the early return above) and
    // its lookup ran the full cascade: entity DB (+ Arkham when live) →
    // exchange detector → TronScan/OKLink (TRX) or GoPlus/Dune (EVM). When
    // every layer comes back empty we surface an explicit "No entity label
    // found" chip instead of silently omitting it, so an officer can tell the
    // address is *unattributed* (we checked) rather than *unchecked*.
    // nearest_exchange is a trace result, not this node's own identity, so it
    // deliberately does not count toward having an identity here.
    const hasEntityIdentity = !!(
        s.entity_name
        || s.subcategory
        || s.wallet_role
        || s.p2p_merchant
        || isGenesisFunder
        || isIssuerFrozen
        || isFreezeTarget
        || (typeof s.telegram_mentions === 'number' && s.telegram_mentions > 0)
        || (typeof s.instagram_hits === 'number' && s.instagram_hits > 0)
        || isSanctioned
        || (nodeLabels.length > 0)
    );

    const velocityProfile = investigation?.sections?.velocity?.status === 'loaded'
        ? investigation.sections.velocity.data?.velocity_profile : undefined;
    const escalationRecommendation = investigation?.sections?.signal_assessment?.status === 'loaded'
        ? investigation.sections.signal_assessment.data?.escalation_recommendation : undefined;

    const riskScoreClass = isSanctioned
        ? 'bg-red-600 text-white'
        : isHighRisk
            ? 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-400'
            : s.risk_level === 'medium'
                ? 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-400'
                : 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-400';

    return (
        <div className="flex flex-wrap items-center gap-1.5 mt-2">
            {/* Risk score */}
            {s.risk_score !== undefined && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onRiskClick}
                        className={`${CHIP_BTN} uppercase tracking-tight ${riskScoreClass}`}
                        title="Open risk details in Overview tab"
                    >
                        {(s.risk_score * 10).toFixed(1)}/10 {s.risk_level || ''}
                    </button>
                    <InfoHint label="Risk score" text={CHIP_HINTS.risk} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* OFAC hit */}
            {isSanctioned && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onSanctionsClick}
                        className={`${CHIP_BTN} bg-red-600 text-white uppercase tracking-wider border-red-700 sanctions-pulse`}
                        data-testid="chip-ofac"
                        title="Open sanctions screening in Intel tab"
                    >
                        <AlertTriangle className="w-3.5 h-3.5" /> <span aria-hidden="true">★</span> OFAC
                    </button>
                    <InfoHint label="OFAC hit" text={CHIP_HINTS.ofac} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Issuer Frozen (On-Chain Blacklist) */}
            {isIssuerFrozen && (
                <span className="inline-flex items-center gap-0.5">
                    <span
                        className={`${CHIP} bg-rose-600 text-white uppercase tracking-wider border-rose-700 shadow-sm font-bold`}
                        title={`On-chain frozen by issuer ${frozenBy && frozenBy.length > 0 ? `(${frozenBy.join(', ')})` : '(Tether/Circle)'}. Address is blacklisted on the token contract.`}
                    >
                        <Snowflake className="w-3.5 h-3.5" /> Frozen ({frozenBy && frozenBy.length > 0 ? frozenBy.join(', ') : 'Tether/Circle'})
                    </span>
                    <InfoHint label="Issuer freeze" text={CHIP_HINTS.issuerFrozen} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Issuer Freeze Petition Target */}
            {isFreezeTarget && (
                <span className="inline-flex items-center gap-0.5">
                    <span
                        className={`${CHIP} bg-orange-100 text-orange-800 dark:bg-orange-900/40 dark:text-orange-300 uppercase border-orange-300 dark:border-orange-800 font-semibold`}
                        title="Unhosted illicit laundering sink / Black USDT pool. Priority target for emergency Issuer Freeze Petition under Section 91 CrPC / Section 94 BNSS to Tether/Circle Compliance."
                    >
                        <ShieldAlert className="w-3.5 h-3.5 text-orange-600 dark:text-orange-400" /> Freeze Target (Petition Tether)
                    </span>
                    <InfoHint label="Freeze target" text={CHIP_HINTS.freezeTarget} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Genesis Funder / Originating Source */}
            {isGenesisFunder && (
                <span className="inline-flex items-center gap-0.5">
                    <span
                        className={`${CHIP} bg-teal-100 text-teal-800 dark:bg-teal-900/40 dark:text-teal-300 uppercase border-teal-300 dark:border-teal-800 font-semibold`}
                        title={`Genesis Funder / Initial gas & activation source${funderEntity ? `: ${funderEntity}` : ''}. Key lead for exchange subpoena & origin of funds.`}
                    >
                        <Zap className="w-3.5 h-3.5 text-teal-600 dark:text-teal-400" /> Genesis Funder{funderEntity ? ` (${funderEntity})` : ''}
                    </span>
                    <InfoHint label="Genesis funder" text={CHIP_HINTS.genesisFunder} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* P2P merchant */}
            {s.p2p_merchant && (
                <span className="inline-flex items-center gap-0.5">
                    <span className={`${CHIP} bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 uppercase border-blue-200 dark:border-blue-800`}>
                        P2P Merchant
                        <HeuristicMarker kind="p2p" basis={HEURISTIC_BASIS.p2p} />
                    </span>
                    <InfoHint label="P2P merchant" text={CHIP_HINTS.p2p} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {typeof s.telegram_mentions === 'number' && s.telegram_mentions > 0 && (
                <span className="inline-flex items-center gap-0.5">
                    <span className={`${CHIP} bg-sky-100 text-sky-800 dark:bg-sky-900/40 dark:text-sky-300 uppercase border-sky-200 dark:border-sky-800`}>
                        Telegram ×{s.telegram_mentions}
                    </span>
                    <InfoHint label="Telegram harvest" text={CHIP_HINTS.telegram} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {typeof s.instagram_hits === 'number' && s.instagram_hits > 0 && (
                <span className="inline-flex items-center gap-0.5">
                    <span className={`${CHIP} bg-pink-100 text-pink-800 dark:bg-pink-900/40 dark:text-pink-300 uppercase border-pink-200 dark:border-pink-800`}>
                        Instagram ×{s.instagram_hits}
                    </span>
                    <InfoHint label="Instagram harvest" text={CHIP_HINTS.instagram} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Subcategory chip — shipped Step 9c (May 2026) when 11 subcategory
                border colours were stripped from the canvas. The data still
                lives on every node; this chip is now its sole canvas-side
                surface. Rendered neutral (no risk colour) — subcategory is
                informational, not a state. */}
            {s.subcategory && (
                <span
                    className={`${CHIP} bg-secondary text-foreground/80 border-border`}
                    title={`Subcategory: ${s.subcategory}`}
                >
                    {s.subcategory
                        .split(/[_\s]+/)
                        .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                        .join(' ')}
                </span>
            )}

            {/* Exchange wallet role (Item 2) — deposit/hot/cold/… A deposit
                wallet usually maps to ONE KYC'd owner, so it reads green as the
                strongest subpoena lead. Informational identity chip. */}
            {LABEL_CLASS_LABEL[labelClass] && (
                <span data-testid="label-class-chip" className={`${CHIP} bg-secondary text-foreground/80 border-border`}>
                    {LABEL_CLASS_LABEL[labelClass]}
                </span>
            )}
            {SOURCE_LABEL[sourceKey] && (
                <span data-testid="label-source-chip" className={`${CHIP} bg-secondary text-foreground/80 border-border`}>
                    {SOURCE_LABEL[sourceKey]}
                </span>
            )}
            {CONFIRMATION_LABEL[confirmation] && (
                <span data-testid="confirmation-chip" className={`${CHIP} bg-secondary text-foreground/80 border-border`}>
                    {CONFIRMATION_LABEL[confirmation]}
                </span>
            )}
            {WALLET_ROLE_LABEL[walletRole] && (
                <span
                    data-testid="wallet-role-chip"
                    className={`${CHIP} uppercase ${walletRoleClass(walletRole)}`}
                    title={`Wallet role: ${WALLET_ROLE_LABEL[walletRole]}`}
                >
                    <Wallet className="w-3.5 h-3.5" /> {WALLET_ROLE_LABEL[walletRole]}
                </span>
            )}

            {/* Nearest exchange */}
            {s.nearest_exchange?.name && s.nearest_exchange.hops !== undefined && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onExchangeClick}
                        className={`${CHIP_BTN} bg-secondary/80 text-foreground uppercase border-border hover:bg-secondary`}
                        title={`Highlight exchange path on graph. ${HEURISTIC_BASIS.nearestExchange}`}
                    >
                        <Network className="w-3.5 h-3.5" /> {s.nearest_exchange.name} ({s.nearest_exchange.hops}h)
                        <HeuristicMarker kind="nearest-exchange" basis={HEURISTIC_BASIS.nearestExchange} />
                    </button>
                    <InfoHint label="Nearest exchange" text={CHIP_HINTS.nearestExchange} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Off-ramp / cash-out point (Item 2) — this address DEPOSITED into a
                CEX. The exchange holds KYC for the depositor, so this is a primary
                subpoena target. Headline investigator signal → violet, prominent. */}
            {s.is_offramp_point && Array.isArray(s.offramp_exchanges) && s.offramp_exchanges.length > 0 && (
                <button
                    onClick={onExchangeClick}
                    className={`${CHIP_BTN} bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 uppercase border-violet-300 dark:border-violet-800 hover:bg-violet-200 dark:hover:bg-violet-900/60`}
                    title={`Off-ramp / cash-out point — this address deposited into ${s.offramp_exchanges.join(', ')}${s.offramp_wallet_role ? ` (${s.offramp_wallet_role} wallet)` : ''}. The exchange holds KYC for the depositor — primary subpoena target.`}
                >
                    <Banknote className="w-3.5 h-3.5" /> Off-ramp → {s.offramp_exchanges.slice(0, 2).join(', ')}
                </button>
            )}

            {/* Velocity profile */}
            {velocityProfile && (
                <span className="inline-flex items-center gap-0.5">
                    <span
                        className={`${CHIP} uppercase ${
                            velocityProfile === 'Relay' ? 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300 border-red-200 dark:border-red-800' :
                            velocityProfile === 'Distributor' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-200 dark:border-amber-800' :
                            velocityProfile === 'Accumulator' ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 border-blue-200 dark:border-blue-800' :
                            'bg-secondary text-muted-foreground border-border'
                        }`}
                        title={HEURISTIC_BASIS.velocity}
                    >
                        {velocityProfile}
                        <HeuristicMarker kind="velocity" basis={HEURISTIC_BASIS.velocity} />
                    </span>
                    <InfoHint label="Velocity profile" text={CHIP_HINTS.velocity} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Escalation recommendation */}
            {escalationRecommendation && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onEscalationClick}
                        className={`${CHIP_BTN} uppercase ${
                            escalationRecommendation === 'ESCALATE' ? 'bg-red-600 text-white border-red-700' :
                            escalationRecommendation === 'INVESTIGATE' ? 'bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300 border-amber-300 dark:border-amber-800' :
                            'bg-secondary text-muted-foreground border-border'
                        }`}
                        title="Open investigation in Analysis tab"
                    >
                        {escalationRecommendation}
                    </button>
                    <InfoHint label="Escalation" text={CHIP_HINTS.escalation} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Bridge interactions */}
            {bridgeInsight && bridgeInsight.bridge_count > 0 && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onBridgeClick}
                        className={`${CHIP_BTN} bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 border-amber-300 dark:border-amber-800 hover:bg-amber-200 dark:hover:bg-amber-900/60`}
                        title={`${bridgeInsight.bridge_count} bridge interaction${bridgeInsight.bridge_count > 1 ? 's' : ''}${bridgeInsight.primary_bridge_family ? ` · primary: ${bridgeInsight.primary_bridge_family}` : ''}`}
                    >
                        Bridge · {bridgeInsight.bridge_count}
                        {bridgeInsight.primary_target_chain && bridgeInsight.primary_target_chain !== 'MULTI' && (
                            <span className="opacity-70">→ {bridgeInsight.primary_target_chain}</span>
                        )}
                    </button>
                    <InfoHint label="Bridge activity" text={CHIP_HINTS.bridge} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Cross-chain */}
            {crossChainInsight && crossChainInsight.supported_count > 1 && (
                <span className="inline-flex items-center gap-0.5">
                    <button
                        onClick={onCrossChainClick}
                        className={`${CHIP_BTN} bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-200 border-indigo-300 dark:border-indigo-800 hover:bg-indigo-200 dark:hover:bg-indigo-900/60`}
                        title={`Active on ${crossChainInsight.supported_count} chains: ${crossChainInsight.preview_chains.join(', ')}`}
                    >
                        <Globe2 className="w-3.5 h-3.5" /> {crossChainInsight.supported_count} chains
                    </button>
                    <InfoHint label="Cross-chain" text={CHIP_HINTS.chains} iconClassName="w-3.5 h-3.5" />
                </span>
            )}

            {/* Entity-name-as-attribution badge — only when we have an entity name
                AND the title row is showing a short / unidentified name. The
                identity card title already shows entity_name when available, so
                this is a redundant safety net for when the title falls back to
                "Unidentified Address". */}
            {/* Entity-name-as-attribution badge — only when we have an entity name
                AND it is NOT a generic cluster tag. */}
            {customerName && (
                <span data-testid="customer-of-chip" className={`${CHIP} bg-violet-100 text-violet-800 dark:bg-violet-900/40 dark:text-violet-200 border-violet-300`}>
                    {customerName}
                </span>
            )}
            {!customerName && !isHighRisk && s.entity_name && !clusterTag && (
                <EntityNameWithCopy
                    address={addr || ''}
                    title={addr || ''}
                    truncate={false}
                    iconClassName="w-3 h-3"
                    className={`${CHIP} bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300 uppercase border-blue-200 dark:border-blue-800`}
                >
                    <span className="inline-flex items-center gap-1">
                        <ShieldCheck className="w-3.5 h-3.5 shrink-0" />
                        {safeStr(s.entity_name)}{s.entity_type ? ` (${safeStr(s.entity_type)})` : ''}
                    </span>
                </EntityNameWithCopy>
            )}

            {/* Reconciliation badge: when the entity name is a generic OKLink
                cluster tag, say so explicitly so investigators are not misled into
                believing this is a verified exchange infrastructure wallet. */}
            {s.entity_name && clusterTag && (
                <span
                    className={`${CHIP} bg-amber-500/10 text-amber-700 dark:text-amber-300 uppercase border-amber-300 dark:border-amber-700`}
                    title="OKLink cluster tag — this address transacted with the named brand's cluster; it is NOT owned by the brand. Treated as a counterparty, not a verified exchange. On-chain tracing is valid."
                >
                    <AlertTriangle className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
                    Cluster Tag (Not Brand-Owned)
                </span>
            )}

            {/* Node labels (max 2) */}
            {nodeLabels.length > 0 && (
                <>
                    {nodeLabels.slice(0, 2).map((lbl: string, idx: number) => (
                        <span key={idx} className={`${CHIP} bg-secondary text-muted-foreground border-border`}>
                            {lbl}
                        </span>
                    ))}
                    {nodeLabels.length > 2 && (
                        <span className="text-xs text-muted-foreground">+{nodeLabels.length - 2}</span>
                    )}
                </>
            )}

            {/* No attribution found — explicit CLEAR state (see hasEntityIdentity).
                Dashed muted chip so it reads as "we checked, found nothing"
                rather than a positive label. */}
            {!hasEntityIdentity && (
                <span
                    className={`${CHIP} bg-secondary text-muted-foreground border-dashed border-border`}
                    title="Entity resolution ran during triage but found no attribution — checked the local entity database and live sources (exchange detector, TronScan/OKLink, GoPlus, Dune). This address is unattributed, not unchecked."
                >
                    <HelpCircle className="w-3.5 h-3.5 shrink-0" />
                    No entity label found
                </span>
            )}

            {/* F-3b: re-resolve (bypass the 24h negative cache + re-query live sources) */}
            {!hasEntityIdentity && addr && (
                <button
                    onClick={reResolveEntity}
                    disabled={reResolving}
                    className={`${CHIP_BTN} border-blue-400/50 text-blue-500 hover:bg-blue-500/10 disabled:opacity-50`}
                    title="Force re-resolve: bypass the 24h negative cache and re-query live sources (Arkham etc.). Use after a provider outage."
                >
                    <RefreshCw className={`w-3.5 h-3.5 shrink-0 ${reResolving ? 'animate-spin' : ''}`} />
                    {reResolving ? 'Re-resolving…' : 'Re-resolve'}
                </button>
            )}
        </div>
    );
};
